import type { ComponentType } from 'react';
import React, { Fragment } from 'react';
import { match } from 'variant';
import { Box, Newline, Text, Link, Alert } from '@wix/cli-ui-kit';
import { outdent } from 'outdent';
import type { ZodIssue } from 'zod';
import type { CliErrorCodeType } from '@wix/cli-error';
import { ErrorMessage, SystemErrorMessage } from './components/ErrorMessage.js';
import { OutdatedVersionMessage } from './components/OutdatedVersionMessage.js';
import { ServeDeprecationMessage } from './components/ServeDeprecationMessage.js';

export function getErrorComponent(
  code: CliErrorCodeType,
  cause: Error | undefined
): ComponentType {
  return match(code, {
    UnknownFailure: ({ error }) => {
      return () => (
        <>
          <Text skin="error">The CLI encountered an unexpected error:</Text>

          {error instanceof Error ? (
            <Text skin="error">{error.message}</Text>
          ) : (
            <Text>{JSON.stringify(error)}</Text>
          )}
        </>
      );
    },
    FailedToGetDeviceCode: () => {
      return () => (
        <SystemErrorMessage message="An error occurred while logging in: Failed to issue a login device code." />
      );
    },
    FailedToGetAuthToken: () => {
      return () => (
        <SystemErrorMessage message="An error occurred while logging in: Failed to issue refresh token." />
      );
    },
    FailedToRefreshTokenForSite: () => {
      return () => (
        <SystemErrorMessage message="An error occurred while logging in: Failed to issue refresh token for site." />
      );
    },
    FailedToRemoveDir: ({ dirPath }) => {
      return () => (
        <ErrorMessage
          message={`Couldn't delete the directory ${dirPath}.`}
          hint={`Delete this directory manually and try again.`}
        />
      );
    },
    FailedToQueryLocales: () => {
      return () => (
        <SystemErrorMessage message="Failed to pull multilingual data." />
      );
    },
    FailedToQueryPublishedContents: () => {
      return () => (
        <SystemErrorMessage message="Failed to query published contents." />
      );
    },
    NoTranslationPublishedContents: () => {
      return () => (
        <ErrorMessage
          message="No translation published contents found."
          hint={`Make sure the "Wix Multilingual" app has published content.`}
        />
      );
    },
    TranslationUnsupportedForProjectType: () => {
      return () => (
        <ErrorMessage message="Translation command only applicable for headless sites." />
      );
    },
    TranslationFileNotFound: ({ filePath }) => {
      return () => (
        <ErrorMessage
          message={`Translation file not found at \`${filePath}\`.`}
          hint={`Create a \`${filePath}\` file in your project and run the command again.`}
          explanation={`The translation file is used to store the translation keys and values for your project.`}
        />
      );
    },
    InvalidTranslationsFileSchema: ({ filePath, zodError }) => {
      return () => (
        <ErrorMessage
          message={outdent`
            Invalid translations file format at \`${filePath}\`:
            The following errors were found:
            ${zodError.errors.map(formatZodIssue).join('\n')}`}
          hint="The translations file should be a JSON object with string values, or a nested object with string values."
        />
      );
    },
    ConnectUnsupportedForProjectType: () => {
      return () => (
        <ErrorMessage message="The connect command is only available for Site projects." />
      );
    },
    FailedToRenewAuthToken: () => {
      return () => (
        <SystemErrorMessage message="Failed to renew access token." />
      );
    },
    FailedToRenewAuthTokenSiteNotFound: ({ siteId }) => {
      return () => (
        <ErrorMessage
          message={`Authenticate failed, your configured development site (${siteId}) could not be accessed.`}
          hint="Make sure the site exists and you still have access to it."
        />
      );
    },
    FailedToRenewAuthTokenRefreshTokenRevoked: () => {
      return () => (
        <ErrorMessage
          message="Failed to renew access token: refresh token has been revoked."
          hint="Log in again to get a new refresh token."
        />
      );
    },
    FailedToLoginWithRefreshToken: () => {
      return () => (
        <ErrorMessage
          message="Failed to login with refresh token."
          hint="Check that your refresh token is valid and not revoked, then try again."
        />
      );
    },
    FailedToRevokeRefreshToken: ({ email }) => {
      return () => (
        <SystemErrorMessage
          message="Couldn't log out: Failed to revoke refresh token."
          explanation={`Wix CLI is still logged in to account ${email}.`}
        />
      );
    },
    FailedToFetchPackageJson: () => {
      return () => (
        <SystemErrorMessage message="Failed to fetch package data." />
      );
    },
    FailedToCreateProjectFolder: ({ packageFolder }) => {
      return () => (
        <ErrorMessage
          cause={cause}
          message={`Failed to create project folder: ${packageFolder}.`}
        />
      );
    },
    LatestVersionOfCreateAppRequired: ({
      currentVersion,
      latestVersion,
      updateCommand,
    }) => {
      return () => (
        <ErrorMessage
          message={
            <>
              Generating a Wix App is only supported using the latest version (
              {latestVersion}) of <Text bold>@wix/create-app</Text> (current:{' '}
              {currentVersion})
            </>
          }
          hint={
            <Text>
              Make sure you are using the following command:
              <Newline />
              <Text bold>{updateCommand}</Text>
              <Newline />
              If the problem persists, try clearing your package manager's
              cache.
            </Text>
          }
        />
      );
    },
    EmptyDirectoryRequiredAppGeneration: ({ folder }) => {
      return () => (
        <ErrorMessage
          message={
            <>
              Generating a Wix App requires an empty directory but{' '}
              <Text bold>{folder}</Text> already exists and isn't empty.
            </>
          }
          hint={
            <Text>
              Select a different project name or delete the existing{' '}
              <Text bold>{folder}</Text> folder.
            </Text>
          }
        />
      );
    },
    InvalidNewPackageName: ({ packageName, validations }) => {
      return () => (
        <ErrorMessage
          message={
            <>
              Generating a Wix App requires a valid NPM package name for the
              generated <Text bold>package.json</Text> <Newline />
              The name <Text bold>{packageName}</Text> failed the following
              validations:
              <Newline />
              {validations.map((validation, id) => (
                <Text key={id}>
                  <Newline />- {validation}
                </Text>
              ))}
            </>
          }
          hint={`Select a different project name that follows the validations above.`}
        />
      );
    },
    SiteSelectorTimedout: () => {
      return () => (
        <ErrorMessage message={`Waiting for app installation timed out.`} />
      );
    },
    TypeErrorInvalidUrl: () => {
      return () => <ErrorMessage message={`Invalid assets URL.`} />;
    },
    UnsupportedPackageManager: ({ packageManagerName }) => {
      return () => (
        <ErrorMessage
          message={`Unsupported package manager detected. Use a supported one.`}
          hint={`Expected Yarn or NPM but ${
            packageManagerName
              ? `got ${packageManagerName}.`
              : `failed to detect a package manager.`
          }`}
        />
      );
    },
    UnsupportedPackageManagerWorkspaces: ({ packageManagerName }) => {
      return () => (
        <ErrorMessage
          message={`Generating a Wix App inside ${packageManagerName} workspaces is not supported.`}
        />
      );
    },
    FailedToGetDeveloperApps: () => {
      return () => (
        <SystemErrorMessage message="Failed to get the list of your applications." />
      );
    },
    FailedToCreateDevCenterApp: () => {
      return () => (
        <SystemErrorMessage message="Failed to register your app in Wix." />
      );
    },
    FailedToCreateDevCenterOAuthApp: () => {
      return () => (
        <SystemErrorMessage message="Failed to register your oauth app in Wix." />
      );
    },
    FailedToUpdateDevCenterOAuthApp: () => {
      return () => (
        <SystemErrorMessage message="Failed to update your oauth app in Wix." />
      );
    },
    FailedToAddPermission: () => {
      return () => <SystemErrorMessage message="Failed to add permission." />;
    },
    FailedToGetPlacements: () => {
      return () => <SystemErrorMessage message="Failed to get placements." />;
    },
    FailedToCreateTunnel: () => {
      return () => <ErrorMessage message="Failed to create tunnel." />;
    },
    FailedToDeleteTunnel: () => {
      return () => <ErrorMessage message="Failed to delete tunnel." />;
    },
    FailedToGetBlocksExtensionsData: () => {
      return () => (
        <ErrorMessage message="Failed to get blocks extensions data." />
      );
    },
    FailedToGetBlocksStructure: () => {
      return () => (
        <SystemErrorMessage message="Failed to get blocks structure." />
      );
    },
    FailedToUpdateBlocksAfterSync: () => {
      return () => (
        <ErrorMessage message="Failed updating blocks service after changes occurred." />
      );
    },
    SitePermissionDenied: ({ siteId }) => {
      return () => {
        return (
          <ErrorMessage
            message={`You don't have permissions to site ${siteId}.`}
          />
        );
      };
    },
    AppPermissionDenied: ({ appId }) => {
      return () => {
        return (
          <ErrorMessage
            message={`You don't have permissions to app ${appId}.`}
          />
        );
      };
    },
    FailedToUpdateTelemetryConfig: () => {
      return () => (
        <ErrorMessage message="Failed to change telemetry preferences." />
      );
    },
    FailedToInstallPackage: ({ packageName }) => {
      return () => (
        <ErrorMessage
          cause={cause}
          message={`Failed to install NPM package ${packageName}.`}
        />
      );
    },
    FailedToResolveNpmDependencies: () => {
      return () => (
        <SystemErrorMessage message="Failed to resolve NPM dependencies." />
      );
    },
    FailedToInstallPackages: () => {
      return () => (
        <ErrorMessage cause={cause} message="Failed to install NPM packages." />
      );
    },
    FailedToRunExternalCommand: ({ file, args }) => {
      const commandString = [file, ...args].join(' ');
      return () => (
        <ErrorMessage
          cause={cause}
          message={`Failed to run external command: ${commandString}.`}
          hint="Make sure the command is available on your machine and try again."
        />
      );
    },
    FailedMigrationToAnyNpm: () => {
      return () => (
        <ErrorMessage
          message="Could not start the migration process."
          explanation="Make sure your `package.json` does not contain any packages under `dependencies`, as the migration can't continue if any are present."
          hint="If you need to keep any of these packages, move them to `devDependencies` before continuing."
        />
      );
    },
    FailedToUninstallPackage: ({ packageName }) => {
      return () => (
        <ErrorMessage
          cause={cause}
          message={`Failed to uninstall NPM package ${packageName}.`}
        />
      );
    },
    FailedToSyncToRevision: ({ revision }) => {
      return () => (
        <ErrorMessage
          message={`Failed to sync the local project to revision ${revision}.`}
        />
      );
    },
    FailedToReadCache: ({ path }) => {
      return () => (
        <ErrorMessage message={`Failed to read cache file at ${path}.`} />
      );
    },
    FailedToWriteCache: ({ path }) => {
      return () => (
        <ErrorMessage message={`Failed to write cache file at ${path}.`} />
      );
    },
    UploadApplicationFailed: () => {
      return () => <ErrorMessage message="Failed to upload the app." />;
    },
    BuiltProjectNeeded: () => {
      return () => (
        <ErrorMessage message="Can't find build output. Execute `wix app build` to build your app before continuing." />
      );
    },
    WixUninstallMissingPackageName: () => {
      return () => <ErrorMessage message="Specify a package name." />;
    },
    FailedToReadJson: ({ filePath }) => {
      return () => (
        <ErrorMessage cause={cause} message={`Failed to read ${filePath}.`} />
      );
    },
    FailedToReadFile: ({ filePath }) => {
      return () => (
        <ErrorMessage cause={cause} message={`Failed to read ${filePath}.`} />
      );
    },
    InvalidJsonFile: ({ filePath }) => {
      return () => (
        <ErrorMessage
          cause={cause}
          message={`Invalid JSON file located at ${filePath}.`}
        />
      );
    },
    FailedToWriteWixLock: () => {
      return () => <ErrorMessage message={`Failed to write 'wix.lock'.`} />;
    },
    FailedToWriteDependenciesMetadata: () => {
      return () => (
        <ErrorMessage
          message={`Failed to write 'dependencies-metadata.json'.`}
        />
      );
    },
    InvalidVeloConfigJson: ({ configFilename }) => {
      return () => (
        <ErrorMessage
          cause={cause}
          message={`Invalid ${configFilename}, make sure it's configured correctly.`}
        />
      );
    },
    DashboardPageConfigNotFound: ({ configPath }) => {
      return () => (
        <ErrorMessage
          message={`Dashboard page config not found at ${configPath}.`}
        />
      );
    },
    EmbeddedScriptConfigNotFound: ({ configPath }) => {
      return () => (
        <ErrorMessage
          message={`Embedded script config not found at ${configPath}.`}
        />
      );
    },
    CustomElementConfigNotFound: ({ configPath }) => {
      return () => (
        <ErrorMessage
          message={`Site widget config not found at ${configPath}.`}
        />
      );
    },
    SitePluginConfigNotFound: ({ configPath }) => {
      return () => (
        <ErrorMessage
          message={`Site plugin config not found at ${configPath}.`}
        />
      );
    },
    BlocksWidgetApiNotFound: ({ folderPath }) => {
      return () => (
        <ErrorMessage
          message={`Blocks widget API file not found at ${folderPath}.`}
        />
      );
    },
    BlocksWidgetConfigNotFound: ({ configPath }) => {
      return () => (
        <ErrorMessage
          message={`Blocks widget config not found at ${configPath}.`}
        />
      );
    },
    BlocksWidgetPanelConfigNotFound: ({ configPath }) => {
      return () => (
        <ErrorMessage
          message={`Blocks widget panel config not found at ${configPath}.`}
        />
      );
    },
    NoExtensionsFound: () => {
      return () => (
        <ErrorMessage
          message="Your project doesn't seem to have any extensions."
          explanation="Extensions are the building blocks of your app."
          hint="Make sure it is configured properly, or read our documentation (​https://wix.to/b7o4Ml0) to start developing your first extension."
        />
      );
    },
    NoPagesDirectoryFound: ({ srcDir }) => {
      return () => (
        <ErrorMessage
          message={
            <>
              No <Text bold>pages</Text> directory was found under{' '}
              <Text bold>{srcDir}</Text>.
            </>
          }
          hint={
            <Text>
              Consider re-cloning the project repository or creating a{' '}
              <Text bold>pages</Text> directory under <Text bold>{srcDir}</Text>
              .
            </Text>
          }
        />
      );
    },
    DashboardPluginConfigNotFound: ({ configPath }) => {
      return () => (
        <ErrorMessage
          message={`Dashboard plugin config not found at ${configPath}.`}
        />
      );
    },
    DashboardMenuPluginConfigNotFound: ({ configPath }) => {
      return () => (
        <ErrorMessage
          message={`Dashboard menu plugin config not found at ${configPath}.`}
        />
      );
    },
    ServicePluginConfigNotFound: ({ configPath }) => {
      return () => (
        <ErrorMessage
          message={`Service plugin config not found at ${configPath}.`}
        />
      );
    },
    DashboardModalConfigNotFound: ({ configPath }) => {
      return () => (
        <ErrorMessage
          message={`Dashboard modal config not found at ${configPath}.`}
        />
      );
    },
    TSConfigNotFound: ({ configPath }) => {
      return () => (
        <ErrorMessage
          message={`TypeScript config not found at \`${configPath}\`.`}
        />
      );
    },
    InvalidTSConfigError: ({ configPath }) => {
      return () => (
        <ErrorMessage
          message={outdent`
            Invalid TypeScript configuration found at \`${configPath}\`.`}
        />
      );
    },
    InvalidConfigSchemaError: ({ configPath, zodError }) => {
      return () => (
        <ErrorMessage
          message={outdent`
            Invalid configuration found at \`${configPath}\`:
            The following errors were found:
            ${zodError.errors.map(formatZodIssue).join('\n')}`}
          hint="Fix the errors above for your configurations to be applied."
        />
      );
    },
    InvalidParamsOverrideSchemaError: ({ paramsOverridePath, zodError }) => {
      return () => (
        <ErrorMessage
          message={outdent`
            Invalid params override found at \`${paramsOverridePath}\`:
            The following errors were found:
            ${zodError.errors.map(formatZodIssue).join('\n')}`}
          hint="Fix the errors above for your params override to be applied."
        />
      );
    },
    InvalidDependenciesJsonSchemaError: ({ path, zodError }) => {
      return () => (
        <ErrorMessage
          message={outdent`
            Invalid configuration found at \`${path}\`:
            The following errors were found:
            ${zodError.errors.map(formatZodIssue).join('\n')}`}
          hint={
            <Text>
              Try to re-install <Text bold>@wix/cli-app</Text> dependency.
            </Text>
          }
        />
      );
    },
    PermissionDenied: () => {
      return () => (
        <ErrorMessage message="Forbidden operation. Missing permissions." />
      );
    },
    FailedToReadProjectFiles: () => {
      return () => (
        <ErrorMessage
          cause={cause}
          message="Failed to read source files: make sure you have an 'src' folder and that it's readable."
        />
      );
    },
    GridAppFilesMaxLengthExceeded: ({ maxSizeViolationsData, filePaths }) => {
      let errorMessage =
        'Found 1 or more project files that are bigger than the allowed limit.';

      if (maxSizeViolationsData?.files.length) {
        errorMessage += ` (${
          maxSizeViolationsData.expectedSize
        }KB): ${maxSizeViolationsData.files
          .map((f) => `${filePaths[f.fileIndex]} (${f.receivedSize}KB)`)
          .join(', ')}`;
      }

      return () => <ErrorMessage message={errorMessage} />;
    },
    GridAppFailedToUpdateFiles: () => {
      return () => (
        <ErrorMessage
          message={outdent`
            Failed to update files.
            Failed to sync changes from local files to the Dev Editor
            Still watching local files and will continue to sync changes...
          `}
        />
      );
    },
    FailedToWatchFiles: () => {
      return () => (
        <ErrorMessage cause={cause} message="Failed to watch files." />
      );
    },
    FailedToCreateGridApp: ({ error }) => {
      return () => (
        <ErrorMessage
          message="Failed to create an isolated environment."
          explanation={error}
        />
      );
    },
    DeviceCodeTimedOut: () => {
      return () => (
        <ErrorMessage message="The device code wasn't approved in the time allocated. Start the login process again." />
      );
    },
    DuplicateComponentIdsError: ({ issues }) => {
      const Issue = ({ issue }: { issue: (typeof issues)[0] }) => {
        const typeToName = (type: (typeof issue)['components'][0]['type']) =>
          match(type, {
            page: () => 'page',
            plugin: () => 'plugin',
            'menu-plugin': () => 'menu plugin',
            'sidebar-category': () => 'sidebar category',
            'embedded-script': () => 'embedded script',
            'custom-element': () => 'custom element',
            'blocks-widget': () => 'blocks widget',
            'blocks-widget-panel': () => 'blocks widget panel',
            modal: () => 'modal',
            topology: () => 'topology',
            'site-plugin': () => 'site plugin',
            monitoring: () => 'monitoring',
            'site-component': () => 'site component',
            'site-component-panel': () => 'site component panel',
          });
        return (
          <Box flexDirection="column">
            <Text>
              <Text bold>Duplicate ID: </Text>
              {issue.id}
            </Text>
            <Text bold>Extensions:</Text>
            {issue.components.map(({ name, path, type }) => (
              <Text key={path}>
                [{typeToName(type)}] {name} ({path})
              </Text>
            ))}
            <Text>
              <Text bold>Possible GUID: </Text>
              {issue.suggestedId}
            </Text>
          </Box>
        );
      };

      return () => (
        <Box flexDirection="column" rowGap={1}>
          <ErrorMessage
            message="Found multiple extensions with the same ID, this might happen if you copy paste folders."
            hint="You can use the provided suggested GUIDs as the ID of the newly created extension."
          />
          {issues.map((issue) => (
            <Issue issue={issue} key={issue.id} />
          ))}
        </Box>
      );
    },
    InvalidResponseData: () => {
      return () => (
        <SystemErrorMessage message="Got unexpected response from server." />
      );
    },
    InvalidCreateAppResponse: () => {
      return () => (
        <ErrorMessage message="`createApp` returned an invalid response." />
      );
    },
    InvalidDeployAppResponse: () => {
      return () => (
        <ErrorMessage message="Got unexpected response from server. Make sure you are running a supported version of the Velo CLI." />
      );
    },
    InvalidGetDeploymentResponse: () => {
      return () => (
        <ErrorMessage message="Got unexpected response from server. Try again later." />
      );
    },
    InvalidGetDeploymentPipelineDescriptionResponse: () => {
      return () => (
        <SystemErrorMessage message="Got unexpected response from server." />
      );
    },
    FailedToGetPagesDiff: () => {
      return () => <ErrorMessage message="Failed to request pages diff." />;
    },
    InvalidViteBuildOutput: () => {
      return () => <ErrorMessage message="Invalid vite build output." />;
    },
    ViteModuleBundleNotFound: () => {
      return () => (
        <ErrorMessage message="Could not find module bundle in vite build output." />
      );
    },
    ViteFileNotFoundInOutput: ({ fileName }) => {
      return () => (
        <ErrorMessage
          message={`Could not find ${fileName} in vite build output.`}
        />
      );
    },
    FailedToCreateBackendDeployment: () => {
      return () => (
        <ErrorMessage message="Failed to create backend deployment." />
      );
    },
    FailedToUpdateBackendDeployment: () => {
      return () => (
        <ErrorMessage message="Failed to update backend deployment." />
      );
    },
    FailedToGetBackendDeployment: () => {
      return () => <ErrorMessage message="Failed to get backend deployment." />;
    },
    FailedToDeployBackend: () => {
      return () => <ErrorMessage message="Failed to deploy backend." />;
    },
    FailedToGetUserInfo: () => {
      return () => <ErrorMessage message="Failed to get user information." />;
    },
    FailedToGetMyAccount: () => {
      return () => (
        <SystemErrorMessage message="Failed to get account information." />
      );
    },
    FailedToCreateDevelopmentSite: () => {
      return () => (
        <ErrorMessage message="Couldn't create a development site." />
      );
    },
    FailedToCreateMetaSiteFromTemplate: () => {
      return () => (
        <ErrorMessage message="Failed to create a metasite from template." />
      );
    },
    FailedToGetDevelopmentSites: () => {
      return () => (
        <ErrorMessage message="Failed to request development sites." />
      );
    },
    FailedToGetDevelopmentSitesLimit: () => {
      return () => (
        <ErrorMessage message="Failed to request development sites limit." />
      );
    },
    FailedToGetRequiredApps: () => {
      return () => (
        <SystemErrorMessage message="Failed to get app requirement info." />
      );
    },
    FailedToGetMarketListing: () => {
      return () => <ErrorMessage message="Failed to get market listing." />;
    },
    FailedToAddRequiredApp: () => {
      return () => <ErrorMessage message="Failed to add required app." />;
    },
    FailedToQuerySites: () => {
      return () => <ErrorMessage message="Failed to request sites list." />;
    },
    FailedToGetSite: () => {
      return () => (
        <ErrorMessage message="Couldn't get information about your configured development site." />
      );
    },
    FailedToDeploySite: () => {
      return () => <ErrorMessage message="Failed to deploy site." />;
    },
    FailedToDeploySitePreview: () => {
      return () => (
        <SystemErrorMessage message="Failed to deploy site preview." />
      );
    },
    FailedToPublishDeployment: () => {
      return () => <SystemErrorMessage message="Failed to publish site." />;
    },
    FailedToSaveDevelopmentSite: ({ path }) => {
      return () => (
        <ErrorMessage
          message="Failed to save the current development site."
          hint={`Make sure the \`${path}\` file has proper permissions and is accessible.`}
        />
      );
    },
    FailedToBuildApp: () => {
      return () => (
        <ErrorMessage
          message={
            <>
              Failed to build the app.
              <Newline />
              {cause?.message}
            </>
          }
          hint={'Check the build output for more information.'}
        />
      );
    },
    FailedToBuildAstroApp: () => {
      return () => (
        <ErrorMessage
          message="Failed to build the application."
          hint="Please check the build output for more information"
        />
      );
    },
    FailedToBuildExtensionsApp: () => {
      return () => (
        <ErrorMessage
          cause={cause}
          message="Failed to build the extensions application."
          hint="Please check the build output for more information"
        />
      );
    },
    InvalidExtensionsBuildMetadata: () => {
      return () => (
        <ErrorMessage
          cause={cause}
          message="@wix/extensions.build() returned invalid build metadata."
          hint="Please make sure the installed @wix/custom-extensions version is compatible with this CLI."
        />
      );
    },
    FailedToGetAppInstallationDetails: ({ appId }) => {
      return () => (
        <SystemErrorMessage
          message={`Failed to fetch site installation details for app ${appId}.`}
        />
      );
    },
    FailedToInstallApp: ({ appId, siteId }) => {
      return () => (
        <SystemErrorMessage
          message={`Failed to install app ${appId} on site ${siteId}.`}
        />
      );
    },
    FailedToInitializeGitRepo: () => {
      return () => (
        <ErrorMessage cause={cause} message="Failed to initialize git repo." />
      );
    },
    FailedToInstallDependencies: () => {
      return () => (
        <ErrorMessage cause={cause} message="Failed to install dependencies." />
      );
    },
    FailedToCommitChanges: () => {
      return () => (
        <ErrorMessage cause={cause} message="Failed to commit changes." />
      );
    },
    FailedToDeleteAuthData: () => {
      return () => (
        <ErrorMessage cause={cause} message="Failed to delete auth data." />
      );
    },
    FailedToDeleteLegacyAuthData: () => {
      return () => (
        <ErrorMessage
          cause={cause}
          message="Failed to delete legacy auth data."
        />
      );
    },
    FailedToSaveAuthData: () => {
      return () => (
        <ErrorMessage cause={cause} message="Failed to save auth data." />
      );
    },
    FailedToSaveApiKeyAuthData: () => {
      return () => (
        <ErrorMessage
          cause={cause}
          message="Failed to save API key auth data."
        />
      );
    },
    FailedToWriteJson: ({ filePath }) => {
      return () => (
        <ErrorMessage
          cause={cause}
          message={`Failed to write JSON at path: ${filePath}.`}
        />
      );
    },
    FailedToWriteFile: ({ filePath }) => {
      return () => (
        <ErrorMessage
          cause={cause}
          message={`Failed to write file at path: ${filePath}.`}
        />
      );
    },
    FailedToCreateDir: ({ dirPath }) => {
      return () => (
        <ErrorMessage
          cause={cause}
          message={`Failed to create a directory at path: ${dirPath}.`}
        />
      );
    },
    TemplatePathNotFound: ({ templatePath }) => {
      return () => (
        <SystemErrorMessage
          message={`Template path not found: ${templatePath}.`}
        />
      );
    },
    LocalTemplatePathNotFound: ({ path }) => {
      return () => (
        <ErrorMessage message={`Local template path not found: ${path}.`} />
      );
    },
    FailedToCloneRepo: () => {
      return () => (
        <ErrorMessage
          cause={cause}
          message="Failed to clone repo."
          hint="Make sure the Git URL is correct and that your local Git is configured properly to access the remote repository."
        />
      );
    },
    InvalidGitUrl: ({ url }) => {
      return () => <SystemErrorMessage message={`Invalid Git URL: ${url}.`} />;
    },
    AuthenticationRequired: () => {
      return () => (
        <ErrorMessage message="You have to already be logged in to run this command." />
      );
    },
    LoginRequired: () => {
      return () => (
        <ErrorMessage message="You are not authenticated, log in by running `wix login`." />
      );
    },
    InvalidApiKey: () => {
      return () => (
        <ErrorMessage message="You used invalid API key to log in." />
      );
    },
    AccountWasDeleted: () => {
      return () => (
        <ErrorMessage message="The account you are trying to log in to has been deleted." />
      );
    },
    SiteConfiguredRequired: () => {
      return () => (
        <ErrorMessage message="You must enter a `--site` in the command options." />
      );
    },
    FailedAddingPackageToWorkspace: () => {
      return () => (
        <ErrorMessage
          message="Couldn't add package to the workspaces configuration. Add it as instructed by your package manager."
          hint={
            <Text>
              Full error can be found in <Text bold>wix-error.log</Text>.
            </Text>
          }
        />
      );
    },
    FailedToGenerateSelfSignedCertificate: () => {
      return () => (
        <ErrorMessage message="Failed to generate a self-signed certificate for development." />
      );
    },
    NonInteractiveTerminal: () => {
      return () => (
        <ErrorMessage message="This command is not supported in non-interactive terminals. Run with --help to see flags for non-interactive usage, or use an interactive terminal (TTY)." />
      );
    },
    FailedToResolveViteOrigin: () => {
      return () => (
        <ErrorMessage message="Failed to resolve the base url for CSS asset urls." />
      );
    },
    FailedToCreateAppRelease: () => {
      return () => (
        <SystemErrorMessage message="Failed to create an app configuration." />
      );
    },
    FailedToParseHttpErrorResponse: () => {
      return () => (
        <SystemErrorMessage message="Unexpected response from server." />
      );
    },
    FailedToReleaseHeadlessProject: () => {
      return () => (
        <SystemErrorMessage message="Failed releasing a headless site and app." />
      );
    },
    AppReleaseConfigViolations: () => {
      return () => (
        <ErrorMessage
          message="App configuration upload failed because of violations."
          hint={
            <Text>
              It may be temporary error, try again shortly. If it fails again,
              reach us on Discord: <Link url="https://discord.gg/devs-on-wix" />
            </Text>
          }
        />
      );
    },
    FailedToUpdateAppRelease: () => {
      return () => (
        <SystemErrorMessage message="Failed to update an app configuration." />
      );
    },
    FailedToUpdateOverrideWithNoSideEffects: () => {
      return () => (
        <SystemErrorMessage message="Failed to update the app preview." />
      );
    },
    FailedToCreateComponents: () => {
      return () => (
        <SystemErrorMessage message="Failed to create components." />
      );
    },
    FailedToFetchPackageDetails: () => {
      return () => (
        <SystemErrorMessage message="Failed to fetch cli package details." />
      );
    },
    FailedToGetResolveNpmDependenciesResult: () => {
      return () => (
        <SystemErrorMessage message={`Failed to resolve "wix.lock" file.`} />
      );
    },
    CliAppVersionMismatch: ({ cliVersion, cliAppVersion }) => {
      return () => (
        <ErrorMessage
          message={
            <Text>
              The versions of <Text bold>@wix/cli</Text> ({cliVersion}) and{' '}
              <Text bold>@wix/cli-app</Text> ({cliAppVersion}) dependencies do
              not match
            </Text>
          }
          hint={
            <Text>
              Align their versions in your <Text bold>package.json</Text> and
              re-install dependencies.
            </Text>
          }
        />
      );
    },
    InvalidDashboardPageRoute: ({ route, errorMessage }) => {
      return () => (
        <ErrorMessage
          message={`Invalid dashboard page route: ${route}.`}
          hint={errorMessage}
        />
      );
    },
    InvalidEmbeddedScriptFolder: ({ path, errorMessage }) => {
      return () => (
        <ErrorMessage
          message={`Invalid embedded script folder: ${path}.`}
          hint={errorMessage}
        />
      );
    },
    InvalidEmbeddedScriptTemplate: ({ templateFilename, errorMessage }) => {
      return () => (
        <ErrorMessage
          message={`Invalid embedded script template in ${templateFilename}.`}
          hint={errorMessage}
        />
      );
    },
    InvalidCustomElementTagName: ({ tagName, errorMessage }) => {
      return () => (
        <ErrorMessage
          message={
            <Text>
              The folder name is used as tagName for your custom element.
              Unfortunately, <Text bold>{tagName}</Text> is invalid tagName.
              Reason: {errorMessage}
            </Text>
          }
          hint={
            <Text>
              You can find rules for tagName here:{' '}
              <Text bold>
                https://developer.mozilla.org/en-US/docs/Web/API/CustomElementRegistry/define#valid_custom_element_names
              </Text>
            </Text>
          }
        />
      );
    },
    InvalidSiteFrontendPackageVersion: ({ packageName, versionRange }) => {
      return () => (
        <ErrorMessage
          message={
            <Text>
              Package <Text bold>{packageName}</Text> has an invalid version
              range <Text bold>{versionRange}</Text> in your{' '}
              <Text bold>package.json</Text> file.
            </Text>
          }
          hint={
            <Text>
              The version range must follow{' '}
              <Text bold>semantic versioning</Text> rules, and it must contain a
              major version.
              <Newline />
              Read more about it here:{' '}
              <Link url="https://docs.npmjs.com/about-semantic-versioning" />
            </Text>
          }
        />
      );
    },
    FailedToCreateSiteOverride: () => {
      return () => (
        <SystemErrorMessage message="Failed to create site override configuration." />
      );
    },
    FailedToGetDeploymentPipelineDescription: () => {
      return () => (
        <SystemErrorMessage message="Failed to get deployment pipeline details." />
      );
    },
    FailedToGetDeploymentStatus: () => {
      return () => (
        <SystemErrorMessage message="Failed to get deployment status." />
      );
    },
    NetworkError: () => {
      return () => (
        <ErrorMessage
          cause={cause}
          message="Network error occurred."
          hint={`Make sure you have an active internet connection and try again shortly.`}
        />
      );
    },
    FailedToGenerateAppMarketQueryParam: () => {
      return () => (
        <SystemErrorMessage message="Failed to get generate appMarket query parameter." />
      );
    },
    FailedToParseLog: () => {
      return () => (
        <SystemErrorMessage message="Failed to parse log from the server." />
      );
    },
    AppForVersionNotFound: ({ version }) => {
      return () => (
        <ErrorMessage
          message={`Could not find app configuration for the provided version: "${version}"`}
          hint={`Make sure that there is an app for this version.`}
        />
      );
    },
    FailedToFetchAppForVersion: () => {
      return () => (
        <SystemErrorMessage message={`Failed to fetch app configuration.`} />
      );
    },
    FailedToFetchLatestProductionVersion: () => {
      return () => (
        <SystemErrorMessage
          message={`Failed to fetch the latest production version for an app.`}
        />
      );
    },
    FailedToGetAppSecret: () => {
      return () => (
        <SystemErrorMessage
          message={`Failed to fetch app keys and configuration.`}
        />
      );
    },
    FailedToGetApp: () => {
      return () => (
        <SystemErrorMessage message={`Failed to fetch app configuration`} />
      );
    },
    FailedToConnectToLogsService: () => {
      return () => (
        <ErrorMessage message={`Failed to connect to logs service.`} />
      );
    },
    FailedToGetLogsTopology: () => {
      return () => (
        <ErrorMessage
          message={`Failed to fetch logs configuration for the given version.`}
          hint={`Try to use a different version.`}
        />
      );
    },
    FailedInstallCloudflareNoAccess: ({ path }) => {
      return () => (
        <ErrorMessage
          message={`Failed to install required binary in "${path}"`}
          hint={`Check that you have required permissions.`}
        />
      );
    },
    LogsConnectionTimeout: () => {
      return () => (
        <ErrorMessage message={`Log session has been closed due to timeout.`} />
      );
    },
    InvalidServicePluginImplementation: () => {
      return () => (
        <ErrorMessage
          message={`Your service plugin implementation is incorrect.`}
        />
      );
    },
    UnknownServicePlugin: ({ name }) => {
      return () => (
        <ErrorMessage
          message={`Unknown service plugin with the name of \`${name}\``}
        />
      );
    },
    MultilingualAppNotInstalled: () => {
      return () => (
        <ErrorMessage
          message={`Multilingual app is not installed.`}
          hint={`Install the "Wix Multilingual" app in your site dashboard to use this command.`}
        />
      );
    },
    FailedToFetchPrimaryLanguage: () => {
      return () => (
        <ErrorMessage
          message="Failed to fetch the primary language for your site."
          hint="Ensure Wix Multilingual is installed and configured on your site."
        />
      );
    },
    FailedToPushTranslations: () => {
      return () => (
        <ErrorMessage
          message="Failed to push translations to Wix Multilingual."
          hint={`Ensure the "Wix Multilingual" app is installed on your site and try again.`}
        />
      );
    },
    FailedToPullTranslations: () => {
      return () => (
        <ErrorMessage
          message="Failed to pull translations from Wix Multilingual."
          hint="Check your internet connection and try again."
        />
      );
    },

    InvalidServicePluginFolder: ({ relativePath }) => {
      return () => (
        <ErrorMessage
          message={`Invalid folder for service plugin in \`${relativePath}\`.`}
        />
      );
    },
    InvalidSiteOverrideID: () => {
      return () => (
        <ErrorMessage
          message={`Failed to read devSiteOverrideId from local app configurations.`}
          hint={`Restart the development environment and make sure that the .wix/app.config.json file exists.`}
        />
      );
    },
    FailedToFindEsbuildOutputFile: () => {
      return () => (
        <ErrorMessage
          message={`Failed to find file in ESBuild output.`}
          hint={`Restart the development environment and try again.`}
        />
      );
    },
    FailedOptimizingWithEsbuild: () => {
      return () => (
        <ErrorMessage
          cause={cause}
          message={`Failed to optimize dependency with ESBuild.`}
          explanation={`Restart the development environment and try again.`}
        />
      );
    },
    FailedReadingEsbuildOutputFile: () => {
      return () => (
        <ErrorMessage
          message={`Failed to read ESBuild's output file.`}
          hint={`Restart the development environment and try again.`}
        />
      );
    },
    CliAppTemplateNotFound: ({ id }) => {
      return () => (
        <ErrorMessage
          message={
            <>
              Template with ID <Text bold>{id}</Text> not found.
            </>
          }
          hint="Make sure you are using the correct template ID."
        />
      );
    },
    FailedToQueryCliAppTemplates: () => {
      return () => (
        <SystemErrorMessage
          message={'Failed to fetch available app templates.'}
        />
      );
    },
    FailedToCreateDevCenterAppFromTemplate: () => {
      return () => (
        <SystemErrorMessage message="Failed to register your app in Wix." />
      );
    },
    FailedToQueryApps: () => {
      return () => <SystemErrorMessage message="Failed to query apps." />;
    },
    FailedToGetSiteInstalledApps: () => {
      return () => (
        <SystemErrorMessage message="Failed to get site installed apps." />
      );
    },
    FailedToGetSiteInstalledAppInstanceId: () => {
      return () => (
        <SystemErrorMessage message="Failed to get site installed app instance ID." />
      );
    },
    FailedToGetClientSpecMap: () => {
      return () => (
        <SystemErrorMessage message="Failed to get site client spec map." />
      );
    },
    DeniedPermissionToGetAppSecret: () => {
      return () => (
        <ErrorMessage
          message="Insufficient permissions."
          hint="Log in with an account that has the required permissions to access this app."
        />
      );
    },
    FailedToCreateVeloApp: () => {
      return () => (
        <SystemErrorMessage message="Failed to create an isolated environment." />
      );
    },
    FailedToUpdateVeloAppFiles: () => {
      return () => (
        <SystemErrorMessage message="Failed to update files in the isolated environment." />
      );
    },
    FailedToGetFilesFromVelo: () => {
      return () => (
        <SystemErrorMessage message="Failed to get initial project files." />
      );
    },
    FailedToOpenBrowser: () => {
      return () => (
        <ErrorMessage
          cause={cause}
          message="Failed to open browser."
          hint="Make sure you have a browser installed, and it's appropriately configured."
        />
      );
    },
    DeprecatedVersion: ({
      currentVersion,
      latestVersion,
      deprecated,
      hasCliAppInstalled,
    }) => {
      return () => (
        <OutdatedVersionMessage
          currentVersion={currentVersion}
          latestVersion={latestVersion}
          deprecated={deprecated}
          hasCliAppInstalled={hasCliAppInstalled}
        />
      );
    },
    FailedToSyncPagesTypes: () => {
      return () => <SystemErrorMessage message="Failed to sync pages types." />;
    },
    FailedToInitializeSiteFsManager: () => {
      return () => (
        <ErrorMessage
          cause={cause}
          message="Failed to initialize site file system manager."
        />
      );
    },
    FailedToGetSiteFsManagerCode: () => {
      return () => (
        <ErrorMessage
          cause={cause}
          message="Failed to parse site code in local file system."
        />
      );
    },
    FailedToGetSiteFsManagerDocument: () => {
      return () => (
        <ErrorMessage
          cause={cause}
          message="Failed to parse site document in local file system."
        />
      );
    },
    FailedToApplySiteFsManagerFsUpdates: () => {
      return () => (
        <ErrorMessage
          cause={cause}
          message="Failed to apply site local file system updates."
        />
      );
    },
    FailedToApplySiteFsManagerExternalUpdates: () => {
      return () => (
        <ErrorMessage
          cause={cause}
          message="Failed to apply site external updates."
        />
      );
    },
    FailedToSyncVeloCLIServerDocumentUpdate: () => {
      return () => (
        <ErrorMessage
          cause={cause}
          message="Failed to sync local file system document updates with the Editor."
        />
      );
    },
    ServeDeprecated: ({ packageManagerRunCmd }) => {
      return () => (
        <ServeDeprecationMessage packageManagerRunCmd={packageManagerRunCmd} />
      );
    },
    CannotFindConfigReferencedAsset: ({ asset, configPath, field }) => {
      return () => (
        <ErrorMessage
          message={`Failed to load asset ${asset}.`}
          hint={`Make sure the asset is correctly configured in \`${configPath}\` under the field \`${field}\`.`}
        />
      );
    },
    FailedToCreateIntellisenseAdapter: () => {
      return () => (
        <ErrorMessage
          cause={cause}
          message="Failed to create intellisense adapter."
        />
      );
    },
    FailedToInitIntellisenseAdapterTypes: () => {
      return () => (
        <ErrorMessage
          cause={cause}
          message="Failed to initialize site types."
        />
      );
    },
    FailedToUpdateIntellisenseAdapterTypes: () => {
      return () => (
        <ErrorMessage cause={cause} message="Failed to update site types." />
      );
    },
    WebMethodOutsideOfProjectDirectory: ({ relativeId }) => {
      return () => (
        <ErrorMessage
          cause={cause}
          message={`Web method "${relativeId}" is outside of the project directory.`}
        />
      );
    },
    FailedToCreateDraftVersion: () => {
      return () => (
        <SystemErrorMessage message="Failed to retrieve release options available for your app." />
      );
    },
    FailedToReleaseDraftVersion: () => {
      return () => (
        <SystemErrorMessage message="Failed to release a new version of your app." />
      );
    },
    WaitForEditorInstallationStateFailure: () => {
      return () => (
        <SystemErrorMessage
          message={`Waiting for the Editor to finish installation timed out.`}
        />
      );
    },
    EditorPendingInstallationCheckTimedout: () => {
      return () => (
        <SystemErrorMessage
          message={`Editor installation details check timed out.`}
        />
      );
    },
    FailedToGetSdkSlotData: () => {
      return () => (
        <SystemErrorMessage
          message={`Unable to retrieve slot details to extend.`}
        />
      );
    },
    FailedToListComponentByComponentType: () => {
      return () => (
        <SystemErrorMessage
          message={`Unable to retrieve the list of available extensions for your app.`}
        />
      );
    },
    FailedToParseBackofficeExtensionContainer: () => {
      return () => (
        <SystemErrorMessage
          message={`Failed to parse backoffice extension containers data.`}
        />
      );
    },
    FailedToParseBackofficePages: () => {
      return () => (
        <SystemErrorMessage
          message={`Failed to parse backoffice pages data.`}
        />
      );
    },
    FailedToCleanDistFolder: () => {
      return () => (
        <ErrorMessage
          cause={cause}
          message={'Failed to clean the dist folder.'}
        />
      );
    },
    FailedToDeployDocument: () => {
      return () => (
        <SystemErrorMessage message="Failed to deploy site document." />
      );
    },
    FailedToEvalBackendExtension: ({ filename, errorMessage }) => {
      return () => (
        <ErrorMessage
          message={`Failed to process file "${filename}", error details below:
${errorMessage}`}
        />
      );
    },
    FailedToGetMonitoringLoaderScript: ({ dsn }) => {
      return () => (
        <SystemErrorMessage
          message={`Failed to get a monitoring loader script for DSN "${dsn}".`}
        />
      );
    },
    InsufficientNonInterractiveReleaseParameters: () => {
      return () => (
        <ErrorMessage
          message={`Release command in non-interactive mode requires following parameter: --version-type.`}
        />
      );
    },
    DeploymentPipelineFailed: ({ tasksDescriptions }) => {
      return () => (
        <>
          <ErrorMessage
            message={`Errors in your site’s code were found while trying to build your custom code. See output below:`}
          />
          <Box paddingLeft={1} paddingTop={1} flexDirection="column">
            {tasksDescriptions.map((task, index) => (
              <Fragment key={index}>
                <Alert type={task.status === 'SUCCESS' ? 'success' : 'error'}>
                  [{index + 1}/{tasksDescriptions.length}] {task.taskName}...
                </Alert>
                {Object.entries(task.description).map(([path, content]) => (
                  <Box
                    key={path}
                    paddingLeft={2}
                    paddingBottom={1}
                    flexDirection="column"
                  >
                    <Text bold>{path}</Text>
                    <Text>{content.trim()}</Text>
                  </Box>
                ))}
              </Fragment>
            ))}
          </Box>
        </>
      );
    },
    FailedToFetchWixLockFile: () => {
      return () => (
        <SystemErrorMessage message={`Failed to fetch "wix.lock" file.`} />
      );
    },
    AppNameArgumentIsInvalid: ({ errorMessage }) => {
      return () => (
        <ErrorMessage
          message={errorMessage}
          hint="Couldn't create a new app with the specified parameters. Modify the parameters or switch to interactive mode."
        />
      );
    },
    HeadlessArgumentIsInvalid: ({ errorMessage }) => {
      return () => (
        <ErrorMessage
          message={errorMessage}
          hint="Couldn't create a new app with the specified parameters. Modify the parameters or switch to interactive mode."
        />
      );
    },
    BusinessNameArgumentIsInvalid: ({ errorMessage }) => {
      return () => (
        <ErrorMessage
          message={errorMessage}
          hint="Couldn't create a new app with provided Business Name. Modify the parameters or switch to interactive mode."
        />
      );
    },
    CannotReleaseMinorInNoninteractive: () => {
      return () => (
        <ErrorMessage
          message={`Minor version release is not allowed.`}
          hint={`Release major version.`}
        />
      );
    },
    SiteComponentConfigNotFound: ({ configPath }) => {
      return () => (
        <ErrorMessage
          message={`Site component config not found at ${configPath}.`}
        />
      );
    },
    SiteComponentPanelConfigNotFound: ({ configPath }) => {
      return () => (
        <ErrorMessage
          message={`Site component panel config not found at ${configPath}.`}
        />
      );
    },
    SiteComponentPanelDoNotExists: ({ panelName }) => {
      return () => (
        <ErrorMessage
          message={`Site component panel with name ${panelName} does not exist.`}
        />
      );
    },
    FailedToImportCliApp: () => {
      return () => (
        <ErrorMessage cause={cause} message="Failed to load `@wix/cli-app`." />
      );
    },
    FailedToIdentifyProgramFlow: ({ reason }) => {
      return () => (
        <ErrorMessage
          cause={cause}
          message={reason}
          hint="Ensure that you are executing the command from inside a project directory."
        />
      );
    },
    FailedToGetAppEnvironmentVariablesByEnvironment: () => {
      return () => (
        <SystemErrorMessage message="Failed to pull environment variables." />
      );
    },
    FailedToBulkUpsertAppEnvironmentVariables: () => {
      return () => (
        <SystemErrorMessage message="Failed to set/update environment variable." />
      );
    },
    FailedToDeleteAppEnvironmentVariableByEnvironmentAndKey: () => {
      return () => (
        <SystemErrorMessage message="Failed to remove environment variable." />
      );
    },
    FailedToUploadStaticFiles: () => {
      return () => (
        <SystemErrorMessage message="Failed to upload static files." />
      );
    },
    FailedCreatingAppProject: () => {
      return () => (
        <SystemErrorMessage message="An error occurred while creating an app project." />
      );
    },
    FailedToGetAppProject: () => {
      return () => (
        <SystemErrorMessage message="An error occurred while getting an app project." />
      );
    },
    FailedToCreateCodestoreProject: () => {
      return () => (
        <SystemErrorMessage message="An error occurred while creating a codestore project." />
      );
    },
    FailedCreatingAppDeployment: () => {
      return () => (
        <SystemErrorMessage message="An error occoured while creating an app deployment." />
      );
    },
    FailedFinalizingAppDeployment: () => {
      return () => (
        <SystemErrorMessage message="An error occoured while completing app deployment." />
      );
    },
    FailedCreatingNewFunnelProject: () => {
      return () => (
        <SystemErrorMessage message="An error occurred while creating a new business." />
      );
    },
    BuildOutputMissing: ({ reason }) => {
      return () => (
        <ErrorMessage
          message="Project build output is missing."
          explanation={reason}
        />
      );
    },
    SiteOutputDirectoryOutsideProject: () => {
      return () => (
        <ErrorMessage message="Your site output directory must be located inside the main project folder. Please update your configuration." />
      );
    },
    FailedToCreateMonitoringVitePlugin: ({ configPath }) => {
      return () => (
        <ErrorMessage
          cause={cause}
          message="Failed to setup monitoring vite plugin."
          hint={`Make sure you have the correct \`monitoring\` configuration in \`${configPath}\`.`}
        />
      );
    },
    FailedToFindAstroConfig: () => {
      return () => (
        <ErrorMessage message="No valid configuration file found. Ensure that an astro.config.{js,cjs,mjs,ts} file exists in your project directory." />
      );
    },
    ProjectIsAlreadyLinked: () => {
      return () => (
        <ErrorMessage message="This project is already connected to Wix, so no changes were made." />
      );
    },
    ProjectIsNotLinkable: () => {
      return () => (
        <ErrorMessage message="Only Astro projects can be linked to Wix. The current project is missing an Astro configuration file." />
      );
    },
    MissingPresetCssForSiteComponent: ({
      componentId,
      presetName,
      elementBasePath,
    }) => {
      return () => (
        <ErrorMessage
          message={`Site component "${componentId}" is missing preset CSS file for the preset key "${presetName}"${
            elementBasePath
              ? ` in the element base path "${elementBasePath}".`
              : '.'
          }`}
        />
      );
    },
    EAccessPermissionDenied: () => {
      return () => (
        <ErrorMessage
          message={`Wix CLI doesn't have enough permission to modify files.`}
          cause={cause}
        />
      );
    },
    FailedToCreateShareUrl: () => {
      return () => (
        <SystemErrorMessage message="Failed to create preview URL." />
      );
    },
    FailedToListShareUrls: () => {
      return () => (
        <SystemErrorMessage message="Failed to list preview URLs." />
      );
    },
    FailedToParseNextVersion: ({ version }) => {
      return () => (
        <SystemErrorMessage
          message={`The version "${version}" produced by the system does not conform to the expected semantic versioning format and could not be parsed.`}
        />
      );
    },
    FailedToDistributeClientManifestUpdate: () => {
      return () => (
        <SystemErrorMessage message="Failed to distribute client manifest update." />
      );
    },
    FailedToCreateRoutesWithMetaSiteUrl: () => {
      return () => (
        <SystemErrorMessage message="Failed to create routes with meta site URL." />
      );
    },
    FailedToGetUrl: () => {
      return () => (
        <SystemErrorMessage message="Failed to get meta site URL." />
      );
    },
    FailedToGetGitHubOnboardingStatus: ({ projectId }) => {
      return () => (
        <SystemErrorMessage
          message={`Failed to get GitHub onboarding status for project ${projectId}.`}
        />
      );
    },
    FailedToInitGitHubRepository: ({ projectId }) => {
      return () => (
        <SystemErrorMessage
          message={`Failed to initialize GitHub repository for project ${projectId}.`}
        />
      );
    },
    InvalidSiteComponentElementItem: ({ elementPath }) => {
      return () => (
        <ErrorMessage
          cause={cause}
          message={`Site component element config is invalid at ${elementPath}.`}
        />
      );
    },
    TrustedAppIsNotMarkedAsInternal: () => {
      return () => (
        <ErrorMessage
          message="Your component configuration is incorrect. It's encountering the error 'scriptAsset is not supported'. This typically indicates that your app isn't recognized as a Wix App."
          hint="To resolve this, visit your app dashboard in Dev Center and ensure the app is marked as 'Developed by Wix'."
        />
      );
    },
    VibeRepositorySetupTimedout: () => {
      return () => (
        <ErrorMessage
          cause={cause}
          message="The Vibe repository setup timed out. Try again."
        />
      );
    },
    DevCommandFailed: () => {
      return () => (
        <ErrorMessage
          cause={cause}
          message="The development command failed to execute. Review the output log above for details."
        />
      );
    },
    FailedToSetCodeIdentifier: () => {
      return () => (
        <ErrorMessage cause={cause} message="Failed to set code identifier." />
      );
    },
    FailedToSetNamespace: () => {
      return () => (
        <ErrorMessage cause={cause} message="Failed to set namespace." />
      );
    },
    FailedToGenerateExtensionConfig: ({ extensionId }) => {
      return () => (
        <ErrorMessage
          cause={cause}
          message={`Failed to generate config for extension "${extensionId}".`}
        />
      );
    },
    FailedToCreateCompanionApp: () => {
      return () => (
        <ErrorMessage
          cause={cause}
          message={`Failed to create a companion app for your project.`}
        />
      );
    },
    MultipleConfigFiles: ({ paths }) => {
      return () => (
        <ErrorMessage
          cause={cause}
          message={outdent`Found multiple Wix config files. Please keep only one:
            ${paths.join('\n')}`}
        />
      );
    },
    ConfigNoDefaultExport: ({ path }) => {
      return () => (
        <ErrorMessage
          cause={cause}
          message={`No default export in Wix config file at "${path}"`}
        />
      );
    },
    InvalidConfigDefinition: ({ reason }) => {
      return () => (
        <ErrorMessage
          cause={cause}
          message="Invalid Wix config definition."
          hint={reason}
        />
      );
    },
    PluginUnknownHook: ({ hook, supportedHooks, pluginName }) => {
      return () => (
        <ErrorMessage
          cause={cause}
          message={`Unknown Wix CLI hook "${hook}" in plugin "${pluginName}".`}
          hint={`Supported hooks: ${supportedHooks
            .map((supportedHook) => `"${supportedHook}"`)
            .join(', ')}`}
        />
      );
    },
    PluginInvalidHookHandler: ({ hook, pluginName }) => {
      return () => (
        <ErrorMessage
          cause={cause}
          message={`Invalid handler for Wix CLI hook "${hook}" in plugin "${pluginName}".`}
          hint="Hook handlers must be functions."
        />
      );
    },
    PluginDuplicateName: ({ name, firstIndex, duplicateIndex }) => {
      return () => (
        <ErrorMessage
          cause={cause}
          message={`Duplicate plugin name "${name}".`}
          hint={`"plugins[${duplicateIndex}].name" conflicts with "plugins[${firstIndex}].name". Plugin names must be unique.`}
        />
      );
    },
    PluginHandlerFailed: ({
      handlerName,
      pluginName,
    }: {
      handlerName: string;
      pluginName: string;
    }) => {
      return () => (
        <ErrorMessage
          cause={cause}
          message={`Plugin "${pluginName}" failed while handling "${handlerName}".`}
        />
      );
    },
  });
}

function formatZodIssue(issue: ZodIssue) {
  const path = issue.path.join('.');

  if (!path) {
    return `- ${issue.message}`;
  }

  return `- \`${path}\`: ${issue.message}`;
}
